import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react'
import { useStateValue } from '@State'
import { saveAnswer } from '../../api'
import { useDragAndDrop } from './utils/useDragAndDrop'
import { FeedbackDisplay } from './utils/FeedbackDisplay'
import { TabSplitLayout } from './utils/TabSplitLayout'
import { SaveIndicator } from './utils/SaveIndicator'
import { ScrollShadow } from './utils/ScrollShadow'
import { FEEDBACK_LABELS, ZONE_ROW, ZONE_BADGE } from './utils/feedbackStyles'
import { stripHtml } from './utils/stripHtml'

/* CSS for grid layout, interactive states, and SVG — cannot be replaced with inline styles */
const BOWTIE_STYLES = `
.ngn-bowtie-container {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  width: 100%;
  min-width: 480px;
  max-width: 1500px;
  margin: auto;
  padding-top: 0.625rem;
  padding-bottom: 0.625rem;
  position: relative;
  border-radius: 10px;
}
.ngn-bowtie-field-column {
  grid-row: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  align-items: center;
  justify-content: center;
}
.ngn-bowtie-field-column.col-left { grid-column: 1; }
.ngn-bowtie-field-column.col-center { grid-column: 2; }
.ngn-bowtie-field-column.col-right { grid-column: 3; }
.ngn-bowtie-field.has-selection {
  cursor: pointer;
}
.ngn-bowtie-field.has-selection:hover {
  border-color: #007bff;
  background-color: #f8f9fa;
}
.ngn-bowtie-field:focus {
  outline: 2px solid #007bff;
}
.ngn-bowtie-field.drag-over {
  border-color: #007bff;
  background-color: #e3f2fd;
}
.ngn-bowtie-field.valid-target {
  border-color: #28a745;
  background-color: #d4edda;
}
.ngn-bowtie-title {
  grid-row: 2;
  text-align: center;
  margin-top: 0.625rem;
  font-size: 1.25rem;
  font-weight: bold;
}
.ngn-bowtie-title.col-left { grid-column: 1; }
.ngn-bowtie-title.col-center { grid-column: 2; }
.ngn-bowtie-title.col-right { grid-column: 3; }
.ngn-bowtie-options-column {
  grid-row: 3;
  display: flex;
  align-items: start;
  justify-content: center;
}
.ngn-bowtie-options-column.col-left { grid-column: 1; }
.ngn-bowtie-options-column.col-center { grid-column: 2; }
.ngn-bowtie-options-column.col-right { grid-column: 3; }
.ngn-bowtie-option:hover {
  background-color: #f8f9fa;
  border-color: #007bff;
}
.ngn-bowtie-option:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}
.ngn-bowtie-option.selected {
  background-color: #d4edda;
  border-color: #28a745;
  box-shadow: 0 1px 3px rgba(40, 167, 69, 0.25);
}
.ngn-bowtie-option.placed {
  opacity: 0.4;
  pointer-events: none;
}
.ngn-bowtie-option.readonly {
  cursor: default;
  opacity: 0.8;
}
.ngn-bowtie-option.saving {
  cursor: wait;
  opacity: 0.7;
}
.ngn-bowtie-svg {
  position: absolute;
  top: 0;
  left: 0;
  pointer-events: none;
}
.ngn-bowtie-svg line {
  stroke: black;
  stroke-width: 2;
  stroke-linecap: square;
}
`

const FIELD_BASE = {
  display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
  width: '75%', maxWidth: '400px', minWidth: '80px',
  padding: '0.2rem', margin: '5px', border: '2px dashed #ddd', borderRadius: '8px',
  backgroundColor: '#fefefe', transition: 'all 0.2s ease', cursor: 'default',
}

// ~3 lines of text: line-height 1.5 * 1rem * 3 + padding
const FIELD_SIDE = { ...FIELD_BASE, minHeight: '5.5rem' }
// ~2 lines of text
const FIELD_CENTER = { ...FIELD_BASE, minHeight: '4rem' }

const FIELD_FB_CORRECT = { ...FIELD_BASE, border: '2px solid #28a745', backgroundColor: 'transparent' }
const FIELD_FB_WRONG = { ...FIELD_BASE, border: '2px solid #d4a017', backgroundColor: 'transparent' }

const PLACED_ITEM_STYLE = {
  userSelect: 'none', borderRadius: '8px', boxSizing: 'border-box',
  margin: 'calc(-0.2rem - 2px)',
  width: 'calc(100% + 0.4rem + 4px)',
  minHeight: 'calc(100% + 0.4rem + 4px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  textAlign: 'center', padding: '0.25rem 0.5rem',
  border: '2px solid #9ca3af', backgroundColor: '#fff',
  boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
}

const ZONE_ROW_BOWTIE = {
  correct: { ...ZONE_ROW.correct, width: '100%', justifyContent: 'center' },
  incorrect: { ...ZONE_ROW.incorrect, width: '100%', justifyContent: 'center' },
  missed: { ...ZONE_ROW.missed, width: '100%', justifyContent: 'center' },
}



function placementsToAnswer(placements) {
  const answer = { left: {}, center: '', right: {} }
  for (const [zoneId, optGuid] of Object.entries(placements)) {
    const [position, idx] = zoneId.split('-')
    if (position === 'center') {
      answer.center = optGuid
    } else {
      answer[position][idx] = optGuid
    }
  }
  return JSON.stringify(answer)
}

function answerToPlacements(answer) {
  const placements = {}
  if (!answer) return placements
  let parsed
  try { parsed = JSON.parse(answer) } catch { return placements }
  if (!parsed || typeof parsed !== 'object') return placements

  if (parsed.left && typeof parsed.left === 'object') {
    for (const [idx, guid] of Object.entries(parsed.left)) {
      if (guid) placements[`left-${idx}`] = guid
    }
  }
  if (parsed.center) {
    placements['center-0'] = parsed.center
  }
  if (parsed.right && typeof parsed.right === 'object') {
    for (const [idx, guid] of Object.entries(parsed.right)) {
      if (guid) placements[`right-${idx}`] = guid
    }
  }
  return placements
}

export const NGNBowtieQuestion = ({ question, renderTabs = true }) => {
  const [state, dispatch] = useStateValue()
  const { coursenum, activitySubmissionId, savingGuids, readonly } = state
  const isSaving = savingGuids.has(question.guid)
  const bowtie = question.data.bowtie

  useEffect(() => {
    let parsed = {}
    try { parsed = JSON.parse(question.answer || '{}') } catch { parsed = {} }
    const leftCount = Object.values(parsed.left || {}).filter(Boolean).length
    const centerCount = parsed.center ? 1 : 0
    const rightCount = Object.values(parsed.right || {}).filter(Boolean).length
    const selected = leftCount + centerCount + rightCount
    const { minSelections, maxSelections } = question.data
    dispatch({
      type: 'SET_FULLY_ANSWERED', guid: question.guid,
      value: selected >= minSelections && selected <= maxSelections
    })
  }, [question.answer])

  const initialPlacements = useMemo(
    () => answerToPlacements(question.answer),
    [question.answer]
  )

  const saveNewAnswer = useCallback(async (newPlacements) => {
    const answerJson = placementsToAnswer(newPlacements)
    dispatch({ type: 'SET_ANSWER', guid: question.guid, answer: answerJson })
    dispatch({ type: 'SAVE_START', guid: question.guid })
    try {
      const response = await saveAnswer(coursenum, activitySubmissionId, question.guid, answerJson)
      dispatch({ type: 'SAVE_SUCCESS', guid: question.guid, payload: response.data })
    } catch (e) {
      dispatch({ type: 'SAVE_ERROR', guid: question.guid })
    }
  }, [coursenum, activitySubmissionId, question.guid, dispatch])

  const canDrop = useCallback((optGuid, zoneId) => {
    const zonePosition = zoneId.split('-')[0]
    for (const opt of bowtie.left.options) {
      if (opt.guid === optGuid) return zonePosition === 'left'
    }
    for (const opt of bowtie.center.options) {
      if (opt.guid === optGuid) return zonePosition === 'center'
    }
    for (const opt of bowtie.right.options) {
      if (opt.guid === optGuid) return zonePosition === 'right'
    }
    return false
  }, [bowtie])

  const {
    placements, selectedGuid, dragOverZone,
    selectOption, handleZoneClick,
    handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop,
    handleOptionKeyDown, handleZoneKeyDown,
    reset, isPlaced,
  } = useDragAndDrop({
    initialPlacements,
    readonly: readonly || isSaving,
    canDrop,
    onPlacementsChange: saveNewAnswer,
  })

  const handleReset = useCallback(() => {
    if (readonly || isSaving) return
    reset()
  }, [readonly, isSaving, reset])

  const allOptions = useMemo(() => {
    const map = {}
    for (const opt of bowtie.left.options) map[opt.guid] = opt.text
    for (const opt of bowtie.center.options) map[opt.guid] = opt.text
    for (const opt of bowtie.right.options) map[opt.guid] = opt.text
    return map
  }, [bowtie])

  const leftZones = useMemo(
    () => Array.from({ length: bowtie.left.numCorrect }, (_, i) => `left-${i}`),
    [bowtie.left.numCorrect]
  )
  const centerZones = ['center-0']
  const rightZones = useMemo(
    () => Array.from({ length: bowtie.right.numCorrect }, (_, i) => `right-${i}`),
    [bowtie.right.numCorrect]
  )

  const { showFeedback } = state
  const { showAnswer } = state.feedbackSettings
  const { correctAnswer } = question
  const showZoneFeedback = showFeedback && showAnswer && correctAnswer

  // Compute the expected guid for each feedback zone.
  const correctByZone = useMemo(() => {
    if (!showZoneFeedback) return {}
    const byZone = {}

    const mapZones = (zones, position) => {
      const correctGuids = position === 'center'
        ? (correctAnswer.center ? [correctAnswer.center] : [])
        : (correctAnswer[position] || [])
      zones.forEach((zoneId, idx) => {
        byZone[zoneId] = correctGuids[idx] || null
      })
    }

    mapZones(leftZones, 'left')
    mapZones(centerZones, 'center')
    mapZones(rightZones, 'right')

    return byZone
  }, [showZoneFeedback, correctAnswer, leftZones, centerZones, rightZones])

  return (
    <>
      <FeedbackDisplay question={question} />
      <style>{BOWTIE_STYLES}</style>
      <div className="position-relative">
        <SaveIndicator questionGuid={question.guid} />
      </div>
      <TabSplitLayout question={question} renderTabs={renderTabs}>
      <div className="d-flex flex-column align-items-start" style={{ width: '100%' }}>
        <div
          role="heading"
          aria-level={3}
          className="mb-2 text-left"
          dangerouslySetInnerHTML={{ __html: question.text }}
        />

        <ScrollShadow label="Scrollable bowtie">
          <BowtieGrid
            leftZones={leftZones}
            centerZones={centerZones}
            rightZones={rightZones}
            bowtie={bowtie}
            placements={placements}
            selectedGuid={selectedGuid}
            dragOverZone={dragOverZone}
            allOptions={allOptions}
            canDrop={canDrop}
            isPlaced={isPlaced}
            isSaving={isSaving}
            readonly={readonly}
            selectOption={selectOption}
            handleZoneClick={handleZoneClick}
            handleDragStart={handleDragStart}
            handleDragEnd={handleDragEnd}
            handleDragOver={handleDragOver}
            handleDragLeave={handleDragLeave}
            handleDrop={handleDrop}
            handleOptionKeyDown={handleOptionKeyDown}
            handleZoneKeyDown={handleZoneKeyDown}
            handleReset={handleReset}
            showZoneFeedback={showZoneFeedback}
            correctByZone={correctByZone}
          />
        </ScrollShadow>
      </div>
      </TabSplitLayout>
    </>
  )
}

const BowtieGrid = ({
  leftZones, centerZones, rightZones,
  bowtie, placements, selectedGuid, dragOverZone, allOptions, canDrop,
  isPlaced, isSaving, readonly,
  selectOption, handleZoneClick,
  handleDragStart, handleDragEnd, handleDragOver, handleDragLeave, handleDrop,
  handleOptionKeyDown, handleZoneKeyDown, handleReset,
  showZoneFeedback, correctByZone,
}) => {
  const containerRef = useRef(null)
  const leftFieldRefs = useRef([])
  const centerFieldRef = useRef(null)
  const rightFieldRefs = useRef([])
  const [svgLines, setSvgLines] = useState([])

  useEffect(() => {
    const timer = setTimeout(() => {
      requestAnimationFrame(() => {
        if (!containerRef.current || !centerFieldRef.current) return
        const containerRect = containerRef.current.getBoundingClientRect()
        const centerRect = centerFieldRef.current.getBoundingClientRect()
        const lines = []

        leftFieldRefs.current.forEach((el, i) => {
          if (!el) return
          const rect = el.getBoundingClientRect()
          lines.push({
            key: `left-${i}`,
            x1: rect.right - containerRect.left + 3,
            y1: rect.top - containerRect.top + rect.height / 2,
            x2: centerRect.left - containerRect.left - 3,
            y2: centerRect.top - containerRect.top + centerRect.height / 2,
          })
        })

        rightFieldRefs.current.forEach((el, i) => {
          if (!el) return
          const rect = el.getBoundingClientRect()
          lines.push({
            key: `right-${i}`,
            x1: rect.left - containerRect.left - 3,
            y1: rect.top - containerRect.top + rect.height / 2,
            x2: centerRect.right - containerRect.left + 3,
            y2: centerRect.top - containerRect.top + centerRect.height / 2,
          })
        })

        setSvgLines(lines)
      })
    }, 50)
    return () => clearTimeout(timer)
  }, [leftZones.length, rightZones.length, placements])

  useEffect(() => {
    const handleResize = () => {
      if (!containerRef.current || !centerFieldRef.current) return
      requestAnimationFrame(() => {
        const containerRect = containerRef.current.getBoundingClientRect()
        const centerRect = centerFieldRef.current.getBoundingClientRect()
        const lines = []

        leftFieldRefs.current.forEach((el, i) => {
          if (!el) return
          const rect = el.getBoundingClientRect()
          lines.push({
            key: `left-${i}`,
            x1: rect.right - containerRect.left + 3,
            y1: rect.top - containerRect.top + rect.height / 2,
            x2: centerRect.left - containerRect.left - 3,
            y2: centerRect.top - containerRect.top + centerRect.height / 2,
          })
        })

        rightFieldRefs.current.forEach((el, i) => {
          if (!el) return
          const rect = el.getBoundingClientRect()
          lines.push({
            key: `right-${i}`,
            x1: rect.left - containerRect.left - 3,
            y1: rect.top - containerRect.top + rect.height / 2,
            x2: centerRect.right - containerRect.left + 3,
            y2: centerRect.top - containerRect.top + centerRect.height / 2,
          })
        })

        setSvgLines(lines)
      })
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [leftZones.length, rightZones.length])

  const renderField = (zoneId, ref) => {
    const placedGuid = placements[zoneId]
    const position = zoneId.split('-')[0]
    const fieldStyle = position === 'center' ? FIELD_CENTER : FIELD_SIDE

    if (showZoneFeedback) {
      const correctGuid = correctByZone[zoneId]
      const isCorrectPlacement = placedGuid && placedGuid === correctGuid
      const fbStyle = isCorrectPlacement
        ? { ...FIELD_FB_CORRECT, minHeight: fieldStyle.minHeight }
        : { ...FIELD_FB_WRONG, minHeight: fieldStyle.minHeight }

      return (
        <div key={zoneId} ref={ref} style={fbStyle}>
          {isCorrectPlacement ? (
            <span style={ZONE_ROW_BOWTIE.correct}>
              <span dangerouslySetInnerHTML={{ __html: allOptions[placedGuid] || '' }} />
              <span style={ZONE_BADGE.correct}>{FEEDBACK_LABELS.correct}</span>
            </span>
          ) : (
            <>
              {placedGuid && (
                <span style={ZONE_ROW_BOWTIE.incorrect}>
                  <span dangerouslySetInnerHTML={{ __html: allOptions[placedGuid] || '' }} />
                  <span style={ZONE_BADGE.incorrect}>{FEEDBACK_LABELS.incorrect}</span>
                </span>
              )}
              {correctGuid && (
                <span style={ZONE_ROW_BOWTIE.missed}>
                  <span dangerouslySetInnerHTML={{ __html: allOptions[correctGuid] || '' }} />
                  <span style={ZONE_BADGE.missed}>{FEEDBACK_LABELS.missed}</span>
                </span>
              )}
            </>
          )}
        </div>
      )
    }

    const isOver = dragOverZone === zoneId
    const isValidTarget = selectedGuid && canDrop(selectedGuid, zoneId)
    return (
      <div
        key={zoneId}
        ref={ref}
        className={[
          'ngn-bowtie-field',
          isOver ? 'drag-over' : '',
          isValidTarget ? 'valid-target' : '',
          selectedGuid ? 'has-selection' : '',
        ].filter(Boolean).join(' ')}
        style={fieldStyle}
        role="listbox"
        tabIndex={0}
        aria-label={`${position} answer zone`}
        onClick={() => handleZoneClick(zoneId)}
        onKeyDown={e => handleZoneKeyDown(e, zoneId)}
        onDragOver={e => handleDragOver(e, zoneId)}
        onDragLeave={e => handleDragLeave(e, zoneId)}
        onDrop={e => handleDrop(e, zoneId)}
      >
        {placedGuid ? (
          <span style={PLACED_ITEM_STYLE} dangerouslySetInnerHTML={{ __html: allOptions[placedGuid] || '' }} />
        ) : (
          <span className="text-muted font-italic small px-2 py-1">Unanswered</span>
        )}
      </div>
    )
  }

  const renderOptionsList = (position, options) => (
    <ul
      className="list-unstyled d-flex flex-column align-items-center justify-content-start w-100 mt-2 p-2 mx-4 rounded border bg-light"
      style={{ gap: '5px', minHeight: '100px' }}
      role="listbox"
      aria-label={`Options for ${stripHtml(bowtie[position].title)}`}
    >
      {options.map(opt => {
        const placed = isPlaced(opt.guid)
        const isSelected = selectedGuid === opt.guid
        return (
          <li
            key={opt.guid}
            className={[
              'ngn-bowtie-option',
              'd-block',
              'border',
              'rounded',
              'w-100',
              'text-center',
              'shadow',
              'px-2',
              'py-1',
              'my-1',
              isSelected ? 'selected' : '',
              placed ? 'placed' : '',
              readonly ? 'readonly' : '',
              isSaving ? 'saving' : '',
            ].filter(Boolean).join(' ')}
            style={{ cursor: 'grab', transition: 'all 0.2s ease', userSelect: 'none' }}
            role="option"
            tabIndex={placed || readonly ? -1 : 0}
            draggable={!readonly && !placed && !isSaving}
            aria-selected={isSelected}
            aria-label={`${stripHtml(opt.text)} - draggable option`}
            onClick={() => !placed && selectOption(opt.guid)}
            onKeyDown={e => !placed && handleOptionKeyDown(e, opt.guid)}
            onDragStart={e => handleDragStart(e, opt.guid)}
            onDragEnd={handleDragEnd}
          >
            <span dangerouslySetInnerHTML={{ __html: opt.text || '' }} />
          </li>
        )
      })}
    </ul>
  )

  const containerHeight = containerRef.current ? containerRef.current.offsetHeight : 0
  const containerWidth = containerRef.current ? containerRef.current.offsetWidth : 0

  return (
    <div className="ngn-bowtie-container" ref={containerRef} style={{ width: '100%' }}>
      {svgLines.length > 0 && (
        <svg
          className="ngn-bowtie-svg"
          height={containerHeight}
          width={containerWidth}
        >
          {svgLines.map(line => (
            <line
              key={line.key}
              x1={line.x1}
              y1={line.y1}
              x2={line.x2}
              y2={line.y2}
            />
          ))}
        </svg>
      )}

      <div className="ngn-bowtie-field-column col-left">
        {leftZones.map((zoneId, i) =>
          renderField(zoneId, el => { leftFieldRefs.current[i] = el })
        )}
      </div>
      <div className="ngn-bowtie-field-column col-center">
        {centerZones.map(zoneId =>
          renderField(zoneId, el => { centerFieldRef.current = el })
        )}
      </div>
      <div className="ngn-bowtie-field-column col-right">
        {rightZones.map((zoneId, i) =>
          renderField(zoneId, el => { rightFieldRefs.current[i] = el })
        )}
      </div>

      <div className="ngn-bowtie-title col-left" dangerouslySetInnerHTML={{ __html: bowtie.left.title || '' }} />
      <div className="ngn-bowtie-title col-center" dangerouslySetInnerHTML={{ __html: bowtie.center.title || '' }} />
      <div className="ngn-bowtie-title col-right" dangerouslySetInnerHTML={{ __html: bowtie.right.title || '' }} />

      <div className="ngn-bowtie-options-column col-left">
        {renderOptionsList('left', bowtie.left.options)}
      </div>
      <div className="ngn-bowtie-options-column col-center">
        {renderOptionsList('center', bowtie.center.options)}
      </div>
      <div className="ngn-bowtie-options-column col-right">
        {renderOptionsList('right', bowtie.right.options)}
      </div>

      {!readonly && (
        <div className="d-flex flex-row align-items-center pt-2" style={{ gridColumn: 1, gridRow: 4 }}>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={handleReset}
            disabled={isSaving}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  )
}
